import { HttpParams, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ICartCheckoutStatus } from '@core/interfaces/cart/cart-checkout-status.interface';
import { ICartSource } from '@core/interfaces/cart/cart-source.interface';
import { ICart } from '@core/interfaces/cart/cart.interface';
import { IDiscount } from '@core/interfaces/cart/discount.interface';
import { SelectEventRequest } from '@core/models/cart/select-event-request.model';
import { AAGUID, ApiErrorResponse, EncodedHttpParams, HttpService } from '@interticket/core';
import { DateTime } from 'luxon';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable()
export class CartSourceService implements ICartSource {

  private readonly cartV1BaseUrl: string = `/v1/cart/web`;
  private readonly cartV2BaseUrl: string = `/v2/cart/web`;
  private readonly discountBaseUrl: string = `/v1/cart/discount`;
  private readonly discountsBaseUrl: string = `/v1/cart/discounts`;
  private readonly giftcardBaseUrl: string = `/v1/cart/giftcard`;
  private readonly selectionBaseUrl: string = `/v1/selection`;

  constructor(
    private httpService: HttpService
  ) { }

  getCart(): Observable<ICart> {
    return this.httpService.get<ICart>(this.cartV1BaseUrl);
  }

  addToCart(selectEventRequest: SelectEventRequest): Observable<Response> {
    return this.httpService.post<Response>(`${this.selectionBaseUrl}/multiselect/web`, selectEventRequest);
  }

  getCartById(cartId: AAGUID): Observable<ICart> {
    return this.httpService.get<ICart>(`${this.cartV1BaseUrl}/get/${cartId}`);
  }

  deleteCart(cartId: AAGUID): Observable<Response> {
    return this.httpService.delete<Response>(`${this.cartV1BaseUrl}/delete/${cartId}`);
  }

  deleteFromCart(selectionIds: AAGUID[]): Observable<Response> {
    let params: HttpParams = new EncodedHttpParams();
    selectionIds.forEach((id: AAGUID) => {
      params = params.append('selectionIds', id);
    });
    return this.httpService.delete<any>(`/v1/cartItems/web`, { params });
  }

  deleteGiftCardsToBuyFromCart(giftCardIds: AAGUID[]): Observable<Response> {
    let params: HttpParams = new EncodedHttpParams();
    giftCardIds.forEach((id: AAGUID) => {
      params = params.append('giftCardIds', id);
    });
    return this.httpService.delete<Response>(`/v1/cartItems/web`, { params });
  }

  deleteGiftCard(couponId: AAGUID): Observable<Response> {
    return this.httpService.delete<Response>(`${this.giftcardBaseUrl}/${couponId}`);
  }

  checkoutCart(paymentMethodId: AAGUID): Observable<Response> {
    return this.httpService.post<Response>(`${this.cartV2BaseUrl}/checkout`, { paymentMethodId });
  }

  getCheckoutStatus(): Observable<ICartCheckoutStatus> {
    const timestamp: number = DateTime.local().toMillis();
    return this.httpService.get<ICartCheckoutStatus>(`${this.cartV2BaseUrl}/checkout/status?t=${timestamp}`);
  }

  getDiscount(couponId: string): Observable<IDiscount | null> {
    return this.httpService.get<IDiscount>(`${this.discountBaseUrl}/check/${couponId}`).pipe(
      catchError((e: ApiErrorResponse) => e?.status === HttpStatusCode.BadRequest ? of(null) : throwError(e))
    );
  }

  applyDiscounts(cartItemIds: AAGUID[], couponId: AAGUID): Observable<Response> {
    return this.httpService.post<Response>(`${this.discountsBaseUrl}`, { couponId, cartItemIds });
  }

  removeDiscount(cartItemId: AAGUID): Observable<Response> {
    return this.httpService.delete<Response>(`${this.discountBaseUrl}/${cartItemId}`);
  }

}
